import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { DashboardFiltersService } from '@services/shared/dashboard-filters.service';
import { LucideAngularModule } from 'lucide-angular';

/**
 * Dashboard period filter: calendar button with a dropdown to pick everything, a year or a month.
 */
@Component({
	selector: 'app-period-picker',
	imports: [LucideAngularModule, TranslocoPipe],
	templateUrl: './period-picker.component.html',
	styleUrl: './period-picker.component.scss',
})
export class PeriodPickerComponent {
	readonly filters = inject(DashboardFiltersService);
	private readonly host = inject(ElementRef<HTMLElement>);

	open = false;
	/** Year shown in the months grid */
	viewYear = this.initialViewYear();

	toggle(): void {
		this.open = !this.open;
		if (this.open) this.viewYear = this.initialViewYear();
	}

	prevYear(): void {
		if (this.viewYear > this.filters.years[0]) this.viewYear--;
	}

	nextYear(): void {
		if (this.viewYear < this.filters.currentYear) this.viewYear++;
	}

	selectAll(): void {
		this.filters.setAll();
		this.open = false;
	}

	selectYear(): void {
		this.filters.setYear(this.viewYear);
		this.open = false;
	}

	selectMonth(month: number): void {
		if (this.isFuture(month)) return;
		this.filters.setMonth(this.viewYear, month);
		this.open = false;
	}

	isFuture(month: number): boolean {
		return this.viewYear === this.filters.currentYear && month > this.filters.currentMonth;
	}

	isSelectedMonth(month: number): boolean {
		return this.filters.selectedPeriod() === `${this.viewYear}-${String(month + 1).padStart(2, '0')}`;
	}

	get isSelectedYear(): boolean {
		return this.filters.selectedPeriod() === String(this.viewYear);
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event): void {
		if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.open = false;
	}

	/** Escape closes the open panel and returns the focus to the button. */
	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (!this.open) return;
		this.open = false;
		this.host.nativeElement.querySelector('.period-button')?.focus();
	}

	private initialViewYear(): number {
		const year = Number(this.filters.selectedPeriod().slice(0, 4));
		return Number.isNaN(year) ? this.filters.currentYear : year;
	}
}
